import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

import {
  Badge,
  EmptyState,
  Button,
  Input,
  Pagination,
  SectionHeader,
  ViewToggle,
  Textarea,
} from "../../../components/ui";
import CentinelaWatchPill from "../components/CentinelaWatchPill";
import RefreshSectionButton from "../components/RefreshSectionButton";
import TabLoader from "../components/TabLoader";
import ResponsiveRow, { ResponsiveHeader } from "../components/ResponsiveRow";
import { useRoutesList } from "../../../features/routes/queries";
import { useTripsList } from "../../../features/trips/queries";
import type { Trip } from "../../../store/admin/tripsStore";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import { useAlert } from "../../../context/AlertContext";
import { useDarkMode } from "../../../context/ThemeContext";
import { panelThemeClasses } from "../../../theme/themeClasses";
import { useForm } from "react-hook-form";

import { TripAddModal } from "../../admin/components/sections/trips/components";
import type { TripFormData } from "../../admin/components/sections/trips/types";
import { createTrip, deleteTrip } from "../../../store/admin/tripsStore";
import { useEndCentinelaTripCustody } from "../../../features/centinela/mutations";
import { useCentinelaTripCustody } from "../../../features/centinela/queries";
import { mapCentinelaTripCustody } from "../services/mapCentinelaCustody";

import FilterCombobox from "../components/FilterCombobox";
import ConfirmDeleteModal from "../components/ConfirmDeleteModal";
import TripCustodyModal from "../components/TripCustodyModal";
import { useWatchedFleet } from "../hooks/useWatchedFleet";
import { useCentinelaDemo } from "../services/centinelaDemoMode";
import {
  buildDemoRoutes,
  buildDemoTrips,
  buildRouteInsights,
  buildTripWatchStates,
  isDemoRecord,
} from "../services/centinelaMocks";
import { TRIP_CUSTODY_META } from "../theme";
import { TRIP_CUSTODY_STATES } from "../types";

const ITEMS_PER_PAGE = 6;
const TRIP_GRID = "lg:grid-cols-[1fr_140px_150px_120px_100px_110px]";
const TITLE_MAX = 60;

const trimTitle = (value: string) =>
  value.length > TITLE_MAX ? `${value.slice(0, TITLE_MAX - 1).trimEnd()}…` : value;
const CUSTODY_FILTERS = ["all", ...TRIP_CUSTODY_STATES] as const;

export default function ViajesSection() {
  const { t, i18n } = useTranslation("common");
  const { t: tAdmin } = useTranslation("adminSections");
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();

  const [search, setSearch] = useState("");
  const [custodyFilter, setCustodyFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Trip | null>(null);
  const [selectedTripForMonitoring, setSelectedTripForMonitoring] = useState<Trip | null>(null);
  const [deletedIds, setDeletedIds] = useState<string[]>([]);

  const endTripCustody = useEndCentinelaTripCustody();
  const targetCustodyQuery = useCentinelaTripCustody(deleteTarget?.id ?? null);
  const targetHasCustody = Boolean(
    mapCentinelaTripCustody(targetCustodyQuery.data).profile.trim(),
  );

  const { data, isLoading, isFetching, refetch } = useTripsList({ limit: 200 });
  const routesQuery = useRoutesList({ limit: 200 });
  const { refresh: refreshFleet, isRefreshing } = useWatchedFleet();

  const refreshSection = () => {
    void refetch();
    void routesQuery.refetch();
    refreshFleet();
  };

  const companyId = useCompaniesStore((state) => state.companyId);
  const forceDemo = useCentinelaDemo();
  const themeClasses = panelThemeClasses();
  const queryClient = useQueryClient();

  // Trip form
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<TripFormData>();

  const watchedValues = watch();

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setLoadingSubmit(false);
    reset();
  };

  const handleAddNew = () => {
    reset();
    setShowAddModal(true);
  };

  const handleCreateTripSubmit = async (data: TripFormData) => {
    setLoadingSubmit(true);

    const tripData = {
      ...data,
      company_id: companyId || "",
    };

    await createTrip(tripData, showAlert, setLoadingSubmit, t, () => {
      handleCloseAddModal();
      queryClient.invalidateQueries({ queryKey: ["trips", "list"] });
      queryClient.invalidateQueries({ queryKey: ["counts"] });
    }, reset);

    setLoadingSubmit(false);
  };

  const trips = useMemo(() => {
    const rows = forceDemo ? buildDemoTrips(t) : (data?.data ?? []);
    return rows.filter((trip) => !deletedIds.includes(trip.id));
  }, [data, forceDemo, t, deletedIds]);

  const watchStates = useMemo(() => {
    const generated = buildTripWatchStates(trips.map((trip) => trip.id), t);
    return generated;
  }, [trips, t]);

  const routeInsights = useMemo(() => {
    const routes = forceDemo ? buildDemoRoutes(t) : (routesQuery.data?.data ?? []);
    return buildRouteInsights(t, routes.map((route) => route.id));
  }, [routesQuery.data, forceDemo, t]);

  const watchOf = (tripId: string) => watchStates.find((item) => item.tripId === tripId);

  const insightOf = (routeId: string | undefined) =>
    routeId ? routeInsights.find((item) => item.routeId === routeId) : undefined;

  const escortedCount = watchStates.filter((item) => item.custody === "active").length;

  const statusOptions = useMemo(() => {
    const labels = new Map<string, string>();
    trips.forEach((trip) => {
      const id = trip.status_id ?? "unknown";
      labels.set(id, trip.status?.name ?? t("monitoreoIa.trips.unknownStatus"));
    });

    return [
      { value: "all", label: t("monitoreoIa.trips.filterAllStatuses") },
      ...[...labels.entries()].map(([value, label]) => ({ value, label })),
    ];
  }, [trips, t]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return trips.filter((trip) => {
      const watch = watchOf(trip.id);
      const matchesCustody = custodyFilter === "all" || watch?.custody === custodyFilter;
      const matchesStatus = statusFilter === "all" || (trip.status_id ?? "unknown") === statusFilter;
      const matchesTerm =
        term.length === 0 ||
        (trip.title ?? "").toLowerCase().includes(term) ||
        (trip.route?.name ?? "").toLowerCase().includes(term) ||
        (watch?.unitName ?? "").toLowerCase().includes(term) ||
        (watch?.driverName ?? "").toLowerCase().includes(term);

      return matchesCustody && matchesStatus && matchesTerm;
    });
  }, [trips, watchStates, search, custodyFilter, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, custodyFilter, statusFilter, viewMode]);

  const totalPages = Math.max(Math.ceil(filtered.length / ITEMS_PER_PAGE), 1);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const timeLabel = (iso: string) =>
    new Date(iso).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" });

  const removeTrip = async (trip: Trip) => {
    if (targetHasCustody) {
      try {
        await endTripCustody.mutateAsync(trip.id);
        toast.info(t("monitoreoIa.trips.custodyEndedBeforeDelete"));
      } catch {
        toast.error(t("monitoreoIa.trips.deleteCustodyFailed"));
        return;
      }
    }

    await deleteTrip(trip.id, showAlert, t, () => {
      setDeletedIds((prev) => [...prev, trip.id]);
      setDeleteTarget(null);
      void refetch();
    });
  };

  const useDraftAsTrip = () => {
    const clean = draft.trim();
    if (clean.length === 0) return;

    reset();
    setValue("title", trimTitle(clean));
    setValue("description", clean);
    setDraft("");
    setShowAddModal(true);
  };

  if (!companyId) {
    return (
      <div className="flex h-full w-full flex-col">
        <SectionHeader
          title={t("monitoreoIa.trips.sectionTitle")}
          subtitle={t("monitoreoIa.trips.sectionSubtitle", { total: 0, escorted: 0 })}
        />
        <div className="flex-1 overflow-y-auto p-3 sm:p-6">
          <div className="mx-auto w-full max-w-6xl">
            <EmptyState
              icon="ri-building-line"
              title={t("monitoreoIa.selectCompany.title")}
              description={t("monitoreoIa.selectCompany.description")}
            />
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <TabLoader label={t("monitoreoIa.trips.loadingTab")} />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col">
      <SectionHeader
        eyebrow={<CentinelaWatchPill />}
        title={t("monitoreoIa.trips.sectionTitle")}
        subtitle={t("monitoreoIa.trips.sectionSubtitle", {
          total: trips.length,
          escorted: escortedCount,
        })}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <RefreshSectionButton
              onRefresh={refreshSection}
              loading={isFetching || routesQuery.isFetching || isRefreshing}
            />
            <Button
              size="sm"
              icon={<i className="ri-add-line" />}
              onClick={handleAddNew}
            >
              {t("monitoreoIa.trips.new")}
            </Button>
          </div>
        }
        filters={
          <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:flex-row sm:items-center sm:gap-2.5">
            <FilterCombobox
              id="viajes-filter-1"
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
              containerClassName="w-full sm:w-48"
            />
            <FilterCombobox
              id="viajes-filter-2"
              value={custodyFilter}
              onChange={setCustodyFilter}
              options={CUSTODY_FILTERS.map((value) => ({
                value,
                label: t(`monitoreoIa.trips.custodyFilters.${value}`),
              }))}
              containerClassName="w-full sm:w-52"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("monitoreoIa.trips.searchPlaceholder")}
              icon={<i className="ri-search-line" />}
              containerClassName="w-full flex-1 max-sm:order-first max-sm:col-span-2"
            />
            <ViewToggle
              value={viewMode}
              onChange={setViewMode}
              className="max-sm:col-span-2"
              labels={{
                grid: t("monitoreoIa.geofences.view.grid"),
                list: t("monitoreoIa.geofences.view.list"),
              }}
            />
          </div>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto w-full max-w-6xl space-y-5">
          <section className="rounded-2xl border border-dashed border-primary-500/50 bg-primary-500/5 p-5">
            <div className="mb-3 flex items-center gap-2">
              <i className="ri-sparkling-line text-primary-500" />
              <h2 className="text-[13px] font-bold text-content">
                {t("monitoreoIa.trips.assistantTitle")}
              </h2>
            </div>

            <p className="mb-4 text-xs text-content-secondary">
              {t("monitoreoIa.trips.assistantDescription")}
            </p>

            <Textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={2}
              placeholder={t("monitoreoIa.trips.assistantPlaceholder")}
              containerClassName="mb-3"
            />

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                disabled={draft.trim().length === 0}
                onClick={useDraftAsTrip}
                icon={<i className="ri-file-text-line" />}
              >
                {t("monitoreoIa.trips.useDraft")}
              </Button>

              <Button
                size="sm"
                variant="secondary"
                icon={<i className="ri-calendar-line" />}
                onClick={handleAddNew}
              >
                {t("monitoreoIa.trips.manual")}
              </Button>

              <Button
                variant="link"
                size="xs"
                onClick={() => setDraft(t("monitoreoIa.trips.assistantExample"))}
              >
                {t("monitoreoIa.routes.useExample")}
              </Button>
            </div>
          </section>

          {filtered.length === 0 ? (
            <EmptyState
              icon="ri-road-map-line"
              title={t("monitoreoIa.trips.empty")}
              description={t("monitoreoIa.trips.emptyDescription")}
            />
          ) : viewMode === "grid" ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {paginated.map((trip) => {
                  const watch = watchOf(trip.id);
                  const custody = watch?.custody ?? "none";
                  const custodyMeta = TRIP_CUSTODY_META[custody];
                  const inProgress = watch ? watch.progressPercent < 100 && custody === "active" : false;

                  return (
                    <div
                      key={trip.id}
                      className="overflow-hidden rounded-2xl border border-line bg-card transition-colors hover:shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2 border-b border-line px-4 py-4">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[13px] font-bold text-content leading-tight">
                            {trip.title ?? t("monitoreoIa.trips.untitled")}
                          </p>
                          <p className="mt-1 truncate text-[11px] text-content-secondary">
                            {trip.route_id
                              ? (() => {
                                  const insight = insightOf(trip.route_id);
                                  return insight && insight.stops.length > 0
                                    ? `${insight.stops[0]?.name} → ${insight.stops[insight.stops.length - 1]?.name}`
                                    : trip.route?.name ?? t("monitoreoIa.trips.noRoute");
                                })()
                              : trip.route?.name ?? t("monitoreoIa.trips.noRoute")}
                          </p>
                        </div>

                        {trip.status?.name && (
                          <span className="shrink-0 rounded-full text-[10px] font-bold px-3 py-1 bg-cyan-500 text-white whitespace-nowrap">
                            {trip.status.name.toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 px-4 py-3 border-b border-line">
                        <span
                          className={`flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-medium ${custodyMeta.chip}`}
                        >
                          <i className={`${custodyMeta.icon} text-[9px]`} />
                          {t(`monitoreoIa.trips.custody.${custody}`)}
                        </span>

                        {isDemoRecord(trip.id) && (
                          <Badge tone="neutral" size="sm">
                            {t("monitoreoIa.demo.badge")}
                          </Badge>
                        )}
                      </div>

                      <dl className="space-y-2.5 px-4 py-3 border-b border-line text-[12px]">
                        <div className="flex items-center justify-between">
                          <dt className="text-content-secondary">{t("monitoreoIa.trips.unit")}</dt>
                          <dd className="text-content font-medium text-right truncate ml-2">{watch?.unitName ?? "—"}</dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-content-secondary">{t("monitoreoIa.units.driver")}</dt>
                          <dd className="text-content font-medium text-right truncate ml-2">{watch?.driverName ?? "—"}</dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-content-secondary">{t("monitoreoIa.trips.departure")}</dt>
                          <dd className="text-content font-medium">{watch ? timeLabel(watch.departureAt) : "—"}</dd>
                        </div>
                        <div className="flex items-center justify-between">
                          <dt className="text-content-secondary">{t("monitoreoIa.trips.eta")}</dt>
                          <dd className="text-content font-medium">{watch ? timeLabel(watch.estimatedArrivalAt) : "—"}</dd>
                        </div>

                        {watch?.actualArrivalAt && (
                          <div className="flex items-center justify-between">
                            <dt className="text-content-secondary">{t("monitoreoIa.trips.actualArrival")}</dt>
                            <dd className="text-success font-medium">{timeLabel(watch.actualArrivalAt)}</dd>
                          </div>
                        )}
                      </dl>

                      {inProgress && watch && (
                        <div className="px-4 py-3 border-b border-line">
                          <div className="flex items-center justify-between mb-2 text-[11px]">
                            <span className="text-content-secondary">{t("monitoreoIa.trips.progress")}</span>
                            <span className="font-bold text-content">{watch.progressPercent}%</span>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-subtle">
                            <div
                              className="h-full rounded-full bg-primary-500 transition-all"
                              style={{ width: `${watch.progressPercent}%` }}
                            />
                          </div>
                          {watch.nextCheckAt && (
                            <p className="mt-2 text-[11px] text-content-secondary">
                              {t("monitoreoIa.trips.nextCheck", {
                                time: timeLabel(watch.nextCheckAt),
                              })}
                            </p>
                          )}
                        </div>
                      )}

                      {watch?.note && (
                        <div className="px-4 py-3 border-b border-line text-[11px]">
                          <div className="flex items-start gap-2">
                            <i className="ri-information-line text-[10px] text-content-secondary shrink-0 mt-0.5" />
                            <p className="text-content-secondary leading-relaxed">{watch.note}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2 px-4 py-3 flex-wrap">
                        <Button
                          size="xs"
                          variant="secondary"
                          onClick={() => navigate("/monitoreo-ia/unidades")}
                        >
                          {t("monitoreoIa.trips.edit")}
                        </Button>

                        {trip.finalizedAt ? null : (
                          <Button
                            size="xs"
                            variant="secondary"
                            onClick={() => setSelectedTripForMonitoring(trip)}
                            icon={<i className="ri-shield-flash-line" />}
                          >
                            {t("monitoreoIa.trips.monitor")}
                          </Button>
                        )}

                        <Button
                          size="xs"
                          className="bg-cyan-500 hover:bg-cyan-600 text-white"
                          onClick={() => navigate("/monitoreo-ia/unidades")}
                        >
                          {t("monitoreoIa.trips.viewOnMap")}
                        </Button>

                        <Button
                          variant="danger"
                          size="xs"
                          iconOnly
                          className="ml-auto rounded-lg"
                          aria-label={t("monitoreoIa.actionsCommon.delete")}
                          onClick={() => setDeleteTarget(trip)}
                          icon={<i className="ri-delete-bin-line" />}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-line bg-card">
                <ResponsiveHeader
                  gridClassName={TRIP_GRID}
                  columns={[
                    { id: "trip", label: t("monitoreoIa.trips.columns.trip") },
                    { id: "route", label: t("monitoreoIa.trips.columns.route") },
                    { id: "unit", label: t("monitoreoIa.trips.columns.unit") },
                    { id: "schedule", label: t("monitoreoIa.trips.columns.schedule") },
                    { id: "status", label: t("monitoreoIa.trips.columns.status"), center: true },
                    { id: "custody", label: t("monitoreoIa.trips.columns.custody"), center: true },
                  ]}
                />

                {paginated.map((trip) => {
                  const watch = watchOf(trip.id);
                  const custody = watch?.custody ?? "none";
                  const custodyMeta = TRIP_CUSTODY_META[custody];

                  return (
                    <ResponsiveRow
                      key={trip.id}
                      gridClassName={TRIP_GRID}
                      className="hover:bg-subtle"
                      primary={
                        <>
                          <p className="truncate text-[13px] font-semibold text-content">
                            {trip.title ?? t("monitoreoIa.trips.untitled")}
                          </p>
                          <p className="truncate text-[10px] text-content-faint">
                            {watch?.createdByAi ? t("monitoreoIa.routes.createdByAi") : ""}
                          </p>
                        </>
                      }
                      cells={[
                        {
                          id: "route",
                          label: t("monitoreoIa.trips.columns.route"),
                          node: (
                            <span className="block truncate text-[11px] text-content-secondary">
                              {trip.route?.name ?? t("monitoreoIa.trips.noRoute")}
                            </span>
                          ),
                        },
                        {
                          id: "unit",
                          label: t("monitoreoIa.trips.columns.unit"),
                          node: (
                            <span className="block truncate text-[11px] text-content-secondary">
                              {watch?.unitName} · {watch?.driverName}
                            </span>
                          ),
                        },
                        {
                          id: "schedule",
                          label: t("monitoreoIa.trips.columns.schedule"),
                          node: (
                            <span className="text-[11px] text-content-secondary">
                              {watch ? `${timeLabel(watch.departureAt)} → ${timeLabel(watch.estimatedArrivalAt)}` : "—"}
                            </span>
                          ),
                        },
                        {
                          id: "status",
                          label: t("monitoreoIa.trips.columns.status"),
                          center: true,
                          node: trip.status?.name ? (
                            <span
                              className="block w-fit rounded-lg border px-2 py-0.5 text-[11px] font-medium"
                              style={{
                                borderColor: `${trip.status.color ?? "rgb(var(--c-border))"}55`,
                                background: `${trip.status.color ?? "rgb(var(--c-subtle))"}1a`,
                                color: trip.status.color ?? "rgb(var(--c-content-secondary))",
                              }}
                            >
                              {trip.status.name}
                            </span>
                          ) : null,
                        },
                        {
                          id: "custody",
                          label: t("monitoreoIa.trips.columns.custody"),
                          center: true,
                          node: (
                            <span
                              className={`flex w-fit items-center gap-1 rounded-lg border px-2 py-0.5 text-[11px] font-medium ${custodyMeta.chip}`}
                            >
                              <i className={`${custodyMeta.icon} text-[9px]`} />
                              {t(`monitoreoIa.trips.custody.${custody}`)}
                            </span>
                          ),
                        },
                      ]}
                    />
                  );
                })}
              </div>

              <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
            </>
          )}
        </div>
      </div>

      {showAddModal && (
        <TripAddModal
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          register={register}
          errors={errors}
          loadingSubmit={loadingSubmit}
          onClose={handleCloseAddModal}
          onSubmit={handleSubmit(handleCreateTripSubmit)}
          t={tAdmin}
          setValue={setValue}
          watchedValues={watchedValues}
          hideNotificationFields
        />
      )}

      {deleteTarget ? (
        <ConfirmDeleteModal
          title={t("monitoreoIa.trips.deleteTitle")}
          name={deleteTarget.title ?? ""}
          onClose={() => setDeleteTarget(null)}
          onConfirm={() => void removeTrip(deleteTarget)}
        />
      ) : null}

      {selectedTripForMonitoring ? (
        <TripCustodyModal
          trip={selectedTripForMonitoring}
          onClose={() => setSelectedTripForMonitoring(null)}
        />
      ) : null}
    </div>
  );
}
